import JSZip from 'jszip'
import { formatContacts, type Contact, type FaqEntry, type ShowcaseInput } from '@k4ia/shared'

/**
 * „Aus Word übernehmen": liest ein Dokument im Aufbau des Word-Exports
 * (showcaseExport.ts) und macht daraus die Felder des Showcase-Formulars.
 * So lässt sich ein Case in Word schreiben oder überarbeiten — oft von
 * jemandem ohne Admin-Zugang — und mit einem Upload übernehmen.
 *
 * Erkannt wird an den Überschriften („Kurzbeschreibung", „Beschreibung" …),
 * nicht an der Formatierung allein: In Word nachbearbeitete Dokumente haben
 * oft verrutschte Formate. Am 09.10. stand beim RFID-Kühlschrank die
 * Kurzbeschreibung samt dem Wort „Beschreibung" in einer einzigen Überschrift,
 * und ein Absatz der Beschreibung war als Überschrift formatiert. Beides wird
 * hier gerade gerückt.
 *
 * Nicht übernommen werden Medien, Wissensdokumente und NFC-Tags — die haben
 * im Admin eigene Wege. Platzhalter wie „[Bitte ergänzen: …]" und „nicht
 * ausgefüllt" fallen weg, damit K4ia sie nie als Inhalt liest.
 */

export interface WordImport {
  form: ShowcaseInput
  /** Was beim Lesen aufgefallen ist — steht im Admin unter dem Knopf. */
  hinweise: string[]
}

interface Absatz {
  text: string
  /** Überschrift-Ebene (1, 2, 3 …), 0 = keine Überschrift. */
  ebene: number
  liste: boolean
  /** Alle Textstücke fett — so setzt der Export die Fragen im FAQ. */
  fett: boolean
}

type Feld =
  | 'shortText'
  | 'longText'
  | 'targetAudience'
  | 'keyMessages'
  | 'useCases'
  | 'faq'
  | 'tabooRules'
  | 'contactPerson'
  | 'visualDescription'
  | 'ignorieren'

const ABSCHNITTE: [string, Feld][] = [
  // Die längeren zuerst: „Kurzbeschreibung" endet auf „beschreibung".
  ['Erkennungsmerkmale für die Kamera', 'visualDescription'],
  ['Weitere Einsatzbereiche', 'useCases'],
  ['Kurzbeschreibung', 'shortText'],
  ['Beschreibung', 'longText'],
  ['Zielgruppe', 'targetAudience'],
  ['Kernbotschaften', 'keyMessages'],
  ['Häufige Fragen', 'faq'],
  ['Nicht sagen', 'tabooRules'],
  ['Ansprechpartner', 'contactPerson'],
  ['Bilder', 'ignorieren'],
  ['Medien', 'ignorieren'],
  ['Wissensdokumente', 'ignorieren'],
  ['NFC-Tags', 'ignorieren'],
]

const PLATZHALTER = /\[\s*bitte\s+ergänzen[^\]]*\]/gi
const HAT_PLATZHALTER = /\[\s*bitte\s+ergänzen[^\]]*\]/i

const entities = (t: string) =>
  t
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&#(\d+);/g, (_, n: string) => String.fromCharCode(Number(n)))
    .replace(/&amp;/g, '&')

/** Die Absätze aus `word/document.xml`, mit dem, was der Import braucht. */
export function absaetzeAus(xml: string): Absatz[] {
  const aus: Absatz[] = []
  for (const p of xml.match(/<w:p[ >][\s\S]*?<\/w:p>/g) ?? []) {
    const stil = /<w:pStyle w:val="([^"]+)"/.exec(p)?.[1] ?? ''
    const stufe = /(?:heading|berschrift)\s*(\d)/i.exec(stil)
    const titel = /^title$/i.test(stil)
    const liste = /<w:numPr>/.test(p) || /list|listen/i.test(stil)
    let text = ''
    let alleFett = true
    let stuecke = 0
    for (const r of p.match(/<w:r[ >][\s\S]*?<\/w:r>/g) ?? []) {
      const fett = /<w:b\/>|<w:b w:val="(?:true|1|on)"\/>/.test(r)
      for (const t of r.match(/<w:t[^>]*>[\s\S]*?<\/w:t>|<w:br\/>|<w:tab\/>/g) ?? []) {
        if (t === '<w:br/>') text += '\n'
        else if (t === '<w:tab/>') text += '\t'
        else {
          const inhalt = entities(t.replace(/<[^>]+>/g, ''))
          if (inhalt.trim()) {
            stuecke++
            if (!fett) alleFett = false
          }
          text += inhalt
        }
      }
    }
    aus.push({
      text: text.replace(/\u00a0/g, ' '),
      ebene: stufe ? Number(stufe[1]) : titel ? 1 : 0,
      liste,
      fett: stuecke > 0 && alleFett,
    })
  }
  return aus
}

const sauber = (t: string) =>
  t
    .replace(PLATZHALTER, '')
    .replace(/[ \t]+/g, ' ')
    .replace(/ *\n */g, '\n')
    .trim()

const leer = (t: string) => !t || /^nicht ausgefüllt$/i.test(t) || t === '—'

/** Welcher Abschnitt beginnt hier — und steht davor noch Text, der zum vorigen gehört? */
function abschnittIn(a: Absatz): { feld: Feld; davor: string } | null {
  const t = a.text.trim()
  for (const [name, feld] of ABSCHNITTE) {
    if (t.toLowerCase() === name.toLowerCase()) return { feld, davor: '' }
  }
  // Nur bei Überschriften: „… am Terminal. Beschreibung" — Text und Überschrift verklebt.
  if (a.ebene >= 2) {
    for (const [name, feld] of ABSCHNITTE) {
      if (t.length > name.length && t.toLowerCase().endsWith(` ${name.toLowerCase()}`)) {
        return { feld, davor: t.slice(0, -name.length).trim() }
      }
    }
  }
  return null
}

/** Liest das erste Showcase aus einem exportierten (oder so aufgebauten) Word-Dokument. */
export function showcaseAusAbsaetzen(absaetze: Absatz[]): WordImport {
  const hinweise: string[] = []
  const start = absaetze.findIndex(
    (a) => a.ebene === 1 && a.text.trim() && !/^showcases$/i.test(a.text.trim()),
  )
  if (start < 0) throw new Error('Kein Showcase gefunden — es fehlt die Überschrift mit dem Titel.')
  const weitere = absaetze.slice(start + 1).filter((a) => a.ebene === 1).length
  if (weitere > 0) {
    hinweise.push(`Das Dokument enthält ${weitere + 1} Showcases — übernommen wurde nur der erste.`)
  }

  const titel = absaetze[start]!.text.trim()
  let slug = ''
  let status: 'draft' | 'live' = 'draft'
  let sortOrder = 0
  const inhalt: Record<Exclude<Feld, 'ignorieren'>, Absatz[]> = {
    shortText: [],
    longText: [],
    targetAudience: [],
    keyMessages: [],
    useCases: [],
    faq: [],
    tabooRules: [],
    contactPerson: [],
    visualDescription: [],
  }
  let aktuell: Feld | null = null
  let verklebt = false
  let alsUeberschrift = false

  for (const a of absaetze.slice(start + 1)) {
    if (a.ebene === 1) break
    const t = a.text.trim()
    if (!t) continue
    const meta = /Slug:\s*([a-z0-9-]+)/i.exec(t)
    if (meta && !aktuell) {
      slug = meta[1]!.toLowerCase()
      status = /^live\b/i.test(t) ? 'live' : 'draft'
      sortOrder = Number(/Sortierung\s+(\d+)/i.exec(t)?.[1] ?? 0)
      continue
    }
    if (/^Adresse am Stand:/i.test(t)) continue
    const neu = abschnittIn(a)
    if (neu) {
      if (neu.davor) {
        verklebt = true
        if (aktuell && aktuell !== 'ignorieren')
          inhalt[aktuell].push({ ...a, text: neu.davor, ebene: 0 })
      }
      aktuell = neu.feld
      continue
    }
    if (!aktuell || aktuell === 'ignorieren') continue
    // Eine Überschrift, die keine ist: Text, der in Word als Überschrift formatiert wurde.
    if (a.ebene === 2) alsUeberschrift = true
    inhalt[aktuell].push(a.ebene === 2 ? { ...a, ebene: 0 } : a)
  }

  if (verklebt) hinweise.push('Eine Überschrift war mit Text verklebt — getrennt.')
  if (alsUeberschrift)
    hinweise.push('Text, der als Überschrift formatiert war, als Absatz übernommen.')
  if (absaetze.some((a) => HAT_PLATZHALTER.test(a.text)))
    hinweise.push('Platzhalter „[Bitte ergänzen …]" weggelassen.')

  const freitext = (liste: Absatz[]) => {
    const teile: string[] = []
    for (const a of liste) {
      const t = sauber(a.text)
      if (leer(t)) continue
      if (a.liste) teile.push(`- ${t.replace(/^[-*•]\s+/, '')}`)
      else if (a.ebene >= 3) teile.push(`# ${t}`)
      else teile.push(t)
    }
    // Aufzählungspunkte hängen am Absatz davor (wie im Admin getippt), sonst Leerzeile.
    return teile.reduce(
      (s, t, i) => (i === 0 ? t : s + (t.startsWith('- ') ? '\n' : '\n\n') + t),
      '',
    )
  }
  const punkte = (liste: Absatz[]) =>
    liste.map((a) => sauber(a.text).replace(/^[-*•]\s+/, '')).filter((t) => !leer(t))

  const faq: FaqEntry[] = []
  for (const a of inhalt.faq) {
    const t = sauber(a.text)
    if (leer(t)) continue
    const frage = a.fett || (t.endsWith('?') && (faq.length === 0 || faq.at(-1)!.answer))
    if (frage) faq.push({ question: t, answer: '' })
    else if (faq.length > 0) {
      const f = faq.at(-1)!
      f.answer = f.answer ? `${f.answer}\n\n${t}` : t
    }
  }
  const ohneAntwort = faq.filter((f) => !f.answer).length
  if (ohneAntwort) hinweise.push(`${ohneAntwort} Frage(n) im FAQ ohne Antwort — bitte ergänzen.`)

  const kontakte: Contact[] = punkte(inhalt.contactPerson).map((t) => {
    const mail = /[^\s<>()]+@[^\s<>()]+/.exec(t)?.[0]
    const name = (mail ? t.replace(mail, '') : t).replace(/[<>()]/g, '').trim()
    return mail ? { name: name || mail, email: mail.toLowerCase() } : { name }
  })

  if (!slug) {
    slug = titel
      .toLowerCase()
      .replace(/ä/g, 'ae')
      .replace(/ö/g, 'oe')
      .replace(/ü/g, 'ue')
      .replace(/ß/g, 'ss')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
    hinweise.push(`Kein Slug im Dokument — aus dem Titel gebildet: ${slug}`)
  }
  const form: ShowcaseInput = {
    slug,
    title: titel,
    shortText: freitext(inhalt.shortText),
    longText: freitext(inhalt.longText),
    targetAudience: freitext(inhalt.targetAudience),
    keyMessages: punkte(inhalt.keyMessages),
    useCases: punkte(inhalt.useCases),
    faq: faq.map((f) => ({ question: f.question, answer: f.answer || '' })),
    tabooRules: punkte(inhalt.tabooRules),
    contactPerson: formatContacts(kontakte),
    visualDescription: freitext(inhalt.visualDescription),
    partnerName: '',
    status,
    sortOrder,
  }
  if (!form.shortText) hinweise.push('Keine Kurzbeschreibung gefunden — bitte ergänzen.')
  return { form, hinweise }
}

/** Liest eine .docx-Datei (aus dem Word-Export oder in seinem Aufbau). */
export async function showcaseAusWord(datei: ArrayBuffer | Uint8Array): Promise<WordImport> {
  let xml: string | undefined
  try {
    xml = await (await JSZip.loadAsync(datei)).file('word/document.xml')?.async('string')
  } catch {
    // unten: keine Word-Datei
  }
  if (!xml) throw new Error('Das ist keine Word-Datei (.docx).')
  return showcaseAusAbsaetzen(absaetzeAus(xml))
}
